import { Dropdown, IDropdownOption, IPanelStyles, IStackTokens, ITextField, ITooltipProps, mergeStyles, Panel, PrimaryButton, ResponsiveMode, ScrollablePane, ScrollbarVisibility, Spinner, Stack, TextField, TooltipHost } from "@fluentui/react";
import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import authModule from "../../../../utilities/AuthModule";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { SharePointFolderContentTypeIds, sharePointInternalFileName } from "../../config/constants";
import { SharePointInputTypes, SharePointMetadataBooleans } from "../../config/sharepointConstants";
import { SharePointInputData } from "../../interfaces/props/SharePointInputData";
import { RootState } from "../../redux";
import { addRegionalSettings } from "../../redux/modules/regional";
import { DefaultValueService } from "../../services/DefaultValueService";
import { getSharePointContentTypes, getSharepointListSchema } from "../../services/SharePointRestServices";
import { SharepointSpServices } from "../../services/SharepointSpServices";
import { SharepointContentTypeHelper } from "../helpers/sharepointContentTypeHelper";
import InputMetaDataFactory from "../directoryComponents/InputMetadataFactory";
import { createDocumentSet, createFolder } from "../helpers/folderCreationHelper";
import { toggleReload } from "../../redux/modules/reload";
import { customToast } from "../CustomToast";
import { ComponentTypes } from "../../config/config";
import { IDocumentSetCreationData } from "../../interfaces/IDocumentSetCreationData";
import { DatasourceValidationHelper } from "../helpers/datasourceValidationHelper";
import generalModule from "../../../../utilities/GeneralModule";
import { useLazyGetItemByUniqueIDQuery } from "../_v2/redux/api/sharepoint/api-sharepoint-slice";
import { getDriveItemSharepointUniqueId } from "../../services/GraphService";
import { newTemplateFilterEnum } from "../enums/newTemplateFilterEnum";
import { tryEncodeUriComponent } from "../../../../utilities/EncodeDecode";
import { ListItemCreationData } from "../../interfaces/ListItemCreationData";
import { boundField, dataSourceItem } from "../../redux/models/reduxModels";
import { DatasourceFieldHelper } from "../helpers/dataSourceFieldHelper";
import { FileFolderNameExtensions } from "../../services/FileFolderNameExtensions";
import { apiAddListItem } from "../../services/ApiService";
import { DatasourceHelper } from "../helpers/dataSourceHelper";
import { useClientTypeContext } from "../hooks/useClientTypeContext";
import { ContentTypeNameDatasourcePicker } from "../inputs/contentTypeNameDatasourcePicker";
import { setDatasourceValues } from "../../redux/modules/datasource";
import { refrehOptionsRequest } from "../../config/datasourceConstants";
import { NewDocumentTemplate } from "../../interfaces/IDocumentTemplate";
import { trimCharacter } from "../helpers/stringHelper";

const loaderStackStyle: React.CSSProperties = {
    paddingTop: 10
}

const inputStackStyle: React.CSSProperties = {
    paddingTop: 10,
    paddingBottom: 15
}

const stackTokens: IStackTokens = {
    childrenGap: 15
};

export const scrollStyle = mergeStyles({
    position: "relative",
    height: "calc(100vh - 135px)",
    marginTop: 5,
    marginBottom: 10
});


const panelStyles: Partial<IPanelStyles> = {
    content: {
        paddingLeft: 5,
        paddingRight: 5,
        paddingBottom: 0
    },
    footerInner: {
        paddingTop: 10
    }
}

const mapStateToProps = (state: RootState) => {
    return {
        workingDirectories: state.workingDirectories.workingDirectories,
        currentDirectory: state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1],
        datasourceValues: state.datasource.values,
        datasourceInitialLoad: state.datasource.initialLoad,
        regionalSettings: state.regionalSettings,
        hiddenMetadataFields: state.tenantInfo?.hiddenMetadataFields,
        enableNewTemplateFilters: state.tenantInfo.enableNewTemplateFilters,
        newTemplateDocumentSetFilters: state.tenantInfo.newTemplateDocumentSetFilters,
        newTemplateFolderFilters: state.tenantInfo.newTemplateFolderFilters,
        newTemplateLibraryFilters: state.tenantInfo.newTemplateLibraryFilters,
        enforceStrictDocumentSetsOnly: state.tenantInfo.enforceStrictDocumentSetsOnly,
        datasourceCallerId: state.datasource?.callerId,
        datasources: state.tenantInfo?.datasources,
        tenantGuid: state.user?.account?.tenantId,
        accountId: state.user?.account?.accountId
    };
};

const mapDispatchToProps = {
    addRegionalSettings,
    toggleReload,
    setDatasourceValues
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface NewPanelProps extends PropsFromRedux {
    isOpen: boolean;
    dismissNewFolderPanel: () => void,
    onFolderCreated?: () => void,
    selectedTemplate?: NewDocumentTemplate;
    serverRelPath?: string;
}

const NewFolderPanel: React.FunctionComponent<NewPanelProps> = ({
    isOpen,
    dismissNewFolderPanel,
    workingDirectories,
    currentDirectory,
    datasourceValues,
    datasourceInitialLoad,
    regionalSettings,
    addRegionalSettings,
    hiddenMetadataFields,
    toggleReload,
    setDatasourceValues,
    onFolderCreated,
    selectedTemplate,
    enableNewTemplateFilters,
    newTemplateDocumentSetFilters,
    newTemplateFolderFilters,
    newTemplateLibraryFilters,
    enforceStrictDocumentSetsOnly,
    datasourceCallerId,
    datasources,
    serverRelPath,
    tenantGuid,
    accountId
}) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isLoading, setLoading] = React.useState<boolean>(false);
    const clientTypes = useClientTypeContext(currentDirectory?.siteId);
    // useMemo, NOT a one-shot useState initializer: the client-type context can settle after
    // mount, and a captured verdict would silently ignore it.
    const datasourceFields = React.useMemo<boundField[]>(
        () => DatasourceFieldHelper.getDatasourceFields(datasources, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""], clientTypes),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clientTypes]
    );
    const [datasourceItem, setDatasourceItem] = React.useState<dataSourceItem>();
    const [sharepointService, setSharepointService] = React.useState<SharepointSpServices>(null);
    const [inputValuesToSave, setInputValuesToSave] = React.useState<SharePointInputData[]>([]);
    const [contentTypes, setContentTypes] = React.useState<IDropdownOption[]>([]);
    const [selectedItem, setSelectedItem] = React.useState<IDropdownOption>();
    const [isMultipleOptionsAvailable, setIsMultipleOptionsAvailable] = React.useState<boolean>(false);
    const [fields, setFields] = React.useState([]);
    const [folderName, setFolderName] = React.useState<string>("");
    const [isCreatingFolder, setIsCreatingFolder] = React.useState<boolean>(false);
    const [isCurrentDirectorySPList, setIsCurrentDirectorySPList] = React.useState<boolean>(false);
    const [datasourceBoundContentTypeInputName, setDatasourceBoundContentTypeInputName] = React.useState<string>();

    const [getItemByUniqueId] = useLazyGetItemByUniqueIDQuery();

    const checkIfInOneDrive = () => {
        if (currentDirectory) {
            return currentDirectory?.type == ComponentTypes.OneDrive || currentDirectory?.type == ComponentTypes.OneDriveSubFolder
        }
        return false;
    }

    const isInOneDrive = React.useRef(checkIfInOneDrive());
    const nameTextFieldRef = React.useRef<ITextField>();

    React.useEffect(() => {
        if (workingDirectories && workingDirectories.some(x => x.type === ComponentTypes.SharePointList)) {
            setIsCurrentDirectorySPList(true);
        }
    }, [workingDirectories]);

    React.useEffect(() => {
        if (isOpen === true) {
            const folderOption = {
                key: SharePointFolderContentTypeIds.folder,
                text: languageStrings.Folder
            }
            const isOneDrive = currentDirectory?.type === ComponentTypes.OneDrive || currentDirectory?.type === ComponentTypes.OneDriveSubFolder;
            if (!isOneDrive) {
                setLoading(true);
                const setupAsyncWrapper = async () => {
                    try {
                        const accessToken = await authModule.getSharePointAccessToken();
                        const sharepointSpService = new SharepointSpServices(currentDirectory.webUrl, accessToken);
                        setSharepointService(sharepointSpService);
                        const regionalSettingsAvailable = regionalSettings[currentDirectory.webUrl];
                        if (!regionalSettingsAvailable) {
                            const regionalSettings = await sharepointSpService.getSiteRegionalSettings();
                            if (regionalSettings) {
                                addRegionalSettings({ ...regionalSettings, webUrl: currentDirectory.webUrl });
                            }
                        }

                        if (!selectedTemplate) {
                            await getContentTypes(accessToken);
                        }
                        else {
                            if (selectedTemplate.templateId === "NewFolder") {
                                setSelectedItem(folderOption);
                            }
                            else if (selectedTemplate.contentTypeId) {
                                setSelectedItem({
                                    key: selectedTemplate.contentTypeId,
                                    text: selectedTemplate.title
                                });
                                await getFields(await authModule.getSharePointAccessToken(), selectedTemplate.contentTypeId);
                            }
                            else {
                                setSelectedItem(folderOption);
                            }
                        }
                    } catch (e) {
                        logger(e, "NewFolderPanel.tsx (useEffect 1)");
                        setSelectedItem(folderOption);
                    }
                    setLoading(false);
                };
                setupAsyncWrapper();
            }
            else {
                setSelectedItem(folderOption);
            }
        }
    }, [isOpen, currentDirectory, selectedTemplate]);

    React.useEffect(() => {
        if (datasources && currentDirectory && selectedItem) {
            const contentTypeInputName: string = `${selectedItem.text} name`;
            const datasourceItem = DatasourceHelper.getInputDataSource(datasources, contentTypeInputName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""], clientTypes);
            if (datasourceItem) {
                setDatasourceBoundContentTypeInputName(contentTypeInputName);
                setDatasourceItem(datasourceItem);
            } else {
                // Must CLEAR, not skip: a client-type verdict that settles to "excluded" after
                // an earlier run resolved the datasource re-fires this effect with undefined,
                // and skipping would leave the stale datasource (and its picker) mounted for
                // the panel's lifetime.
                setDatasourceBoundContentTypeInputName(undefined);
                setDatasourceItem(undefined);
            }
        }
    }, [datasources, currentDirectory, selectedItem, clientTypes]);

    React.useEffect(() => {
        if (datasourceValues) {
            updateMetadataPropertiesFromDataSource(datasourceValues);
        }
    }, [datasourceItem, datasourceBoundContentTypeInputName, datasourceValues]);

    const updateMetadataPropertiesFromDataSource = (values: any) => {
        const isMultipleValues: boolean = Array.isArray(values) && values.length > 0;
        const propertyKeys = isMultipleValues
            ? Object.keys(values[0])
            : Object.keys(values);

        if (propertyKeys.length > 0) {
            if (datasourceItem && datasourceBoundContentTypeInputName) {
                const propKey: string = propertyKeys.find(propKey => datasourceBoundContentTypeInputName.trim().toLowerCase() === propKey.trim().toLowerCase());
                if (propKey) {
                    const newContentTypeNameValue: string = values[propKey];
                    updateFolderName(newContentTypeNameValue);
                }
            }

            const updatedinputValuesToSave = [...inputValuesToSave];
            propertyKeys.forEach((propKey) => {
                let updatedValue: any;
                if (isMultipleValues) {
                    const uniqueValues = values
                        .map(value => value[propKey])
                        .filter((value, index, self) => self.indexOf(value) === index);
                    updatedValue = uniqueValues;
                }
                else {
                    updatedValue = values[propKey];
                }
                updateDatasourceDisplayNameInputData(updatedinputValuesToSave, propKey, updatedValue, isMultipleValues);
            });
            setInputValuesToSave(updatedinputValuesToSave);
        }
        setDatasourceValues(null);
    };

    const updateDatasourceDisplayNameInputData = (updatedinputValuesToSave: SharePointInputData[], propKey: string, value: any, isMultipleValues: boolean) => {
        let inputDatasourceId: number;
        let inputToUpdate = inputValuesToSave.find(
            (item) => {
                const boundName: string = item.displayName?.trim()?.toLowerCase();
                if (boundName && item.internalName !== sharePointInternalFileName && boundName === propKey.trim().toLowerCase()) {
                    inputDatasourceId = datasourceCallerId && DatasourceHelper.getInputDataSource(datasources, boundName, [currentDirectory?.siteDisplayName ?? ""], [currentDirectory?.DocumentLibrary ?? ""], clientTypes)?.id;
                    return true;
                }
                return false;
            }
        );

        if (inputToUpdate) {
            const inputDatasourceCompatible: boolean = Number.isInteger(inputDatasourceId) && inputDatasourceId === datasourceCallerId;
            if (value === refrehOptionsRequest) {
                const updatedInput: SharePointInputData = inputDatasourceCompatible && updatedinputValuesToSave[inputToUpdate.index];
                if (updatedInput) {
                    updatedInput.resetDatasourceOptionsToggle = !updatedInput.resetDatasourceOptionsToggle;
                }
            }
            else if (isMultipleValues || inputDatasourceCompatible) { //value param is an array
                try {
                    const updatedInput: SharePointInputData = inputDatasourceCompatible && updatedinputValuesToSave[inputToUpdate.index];
                    if (updatedInput) {
                        if (!Array.isArray(value)) {
                            value = [value];
                        }
                        if (updatedInput.value) {
                            if (Array.isArray(updatedInput.value) && updatedInput.value.length > 0) {
                                updatedInput.value = updatedInput.value
                                    .map(val => {
                                        if (val && value.some(x => x?.trim().toLowerCase() === val.trim().toLowerCase())) {
                                            return val;
                                        }
                                        return null;
                                    })
                                    .filter(val => val);
                            }
                            else if (!datasourceInitialLoad) {
                                if (value.length === 1) {
                                    DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value[0]);
                                }
                                else if ((value.length > 1 && !value.some(x => x?.trim().toLowerCase() === updatedInput?.value?.trim().toLowerCase()))) {
                                    updatedInput.value = null;
                                }
                            }
                        }
                        else if (value.length === 1) {
                            DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value[0]);
                        }
                        updatedInput.updatedDatasourceOptions = value;
                    }
                } catch (error) {
                    logger(error, "NewFolderPanel.tsx (updateDatasourceDisplayNameInputData)");
                }
            }
            else {
                // Incompatible inputs (different datasource, or one EXCLUDED by client type -
                // its id resolves undefined above) receive single values like any plain field,
                // while the datasource-specific option/reset branches above no-op for them.
                // That split is the pre-existing cross-datasource behavior; an excluded input
                // is deliberately treated as a plain field, not specially suppressed.
                DatasourceValidationHelper.setSingleDatasourceResultValue(updatedinputValuesToSave, inputToUpdate, value);
            }
        }
    };

    const getContentTypes = async (accessToken: string) => {
        let multipleOptions: boolean = false;

        let contentTypeResult: any;
        let selectedItem: IDropdownOption;

        const folderOption = {
            key: SharePointFolderContentTypeIds.folder,
            text: languageStrings.Folder
        }

        if (enableNewTemplateFilters) {
            contentTypeResult = (await getSharepointListSchema(accessToken, currentDirectory.webUrl, currentDirectory.sharePointListId))?.NewDocumentTemplates;
            const newDocumentTemplates = JSON.parse(contentTypeResult);

            let availableOptions: IDropdownOption<any>[] = [];

            if (newDocumentTemplates?.length > 0) {
                multipleOptions = true;
                let { addFolder, addDocset } = canAddFolderDocSet();

                if (enforceStrictDocumentSetsOnly
                    && addFolder
                    && newDocumentTemplates.filter(x => x.contentTypeId.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                    const isInDocumentSet: boolean = currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet);
                    const isInFolder = isInDocumentSet && ((currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath) || (currentDirectory?.directory?.length > 0 && currentDirectory.parentId));
                    if (!isInDocumentSet && !isInFolder) {
                        addFolder = false;
                    }
                }

                if (addFolder) {
                    const folders = newDocumentTemplates.filter(x => x.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && !x.contentTypeId.startsWith(SharePointFolderContentTypeIds.documentSet));
                    const folderTemplate = newDocumentTemplates.find(x => x.templateId === "NewFolder");

                    if (folderTemplate && (folderTemplate.isVisible === true || folderTemplate.visible === true)) {
                        availableOptions.push(folderOption);
                    }

                    folders.forEach(folder => {
                        availableOptions.push({
                            key: folder.contentTypeId,
                            text: folder.title
                        })
                    });
                }

                if (addDocset && !workingDirectories?.some(dir => dir?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                    const docSets = newDocumentTemplates.filter(x => x.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet));
                    docSets.forEach(docSet => {
                        availableOptions.push({
                            key: docSet.contentTypeId,
                            text: docSet.title
                        })
                    });
                }

                setContentTypes(availableOptions);
                if (availableOptions?.length > 0) {
                    selectedItem = availableOptions[0];
                    if (selectedItem?.key && selectedItem.key.toString() !== SharePointFolderContentTypeIds.folder) {
                        await getFields(await authModule.getSharePointAccessToken(), selectedItem.key);
                    }
                }
            }
        } else {
            contentTypeResult = await getSharePointContentTypes(accessToken, currentDirectory.webUrl, currentDirectory.sharePointListId);

            if (contentTypeResult?.length > 0) {
                let allowFolders: boolean = true;
                let folderContentTypes = contentTypeResult?.filter(contentType => !contentType.Sealed && contentType.Id?.StringValue?.startsWith(SharePointFolderContentTypeIds.folder));
                if (enforceStrictDocumentSetsOnly
                    && folderContentTypes?.length > 0
                    && folderContentTypes?.some(contentType => contentType.Id?.StringValue?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                    const isInDocumentSet: boolean = currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet);
                    const isInFolder = isInDocumentSet && ((currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath) || (currentDirectory?.directory?.length > 0 && currentDirectory.parentId));
                    if (!isInDocumentSet && !isInFolder) {
                        allowFolders = false;
                        folderContentTypes = folderContentTypes.filter(contentType => contentType?.Id?.StringValue?.startsWith(SharePointFolderContentTypeIds.documentSet));
                    }
                }

                if (folderContentTypes?.length > 0) {
                    multipleOptions = true;

                    let folderContentTypeOptions = SharepointContentTypeHelper.mapFolderContentTypesToDropdownOptions(folderContentTypes);
                    if (allowFolders) {
                        folderContentTypeOptions = [folderOption, ...folderContentTypeOptions];
                    }

                    //remove documet set options if there is a docset in working directories 
                    if (workingDirectories?.some(dir => dir?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
                        folderContentTypeOptions = folderContentTypeOptions.filter(x => !(x.key as string)?.startsWith(SharePointFolderContentTypeIds.documentSet))
                    }

                    setContentTypes(folderContentTypeOptions);
                    selectedItem = folderContentTypeOptions[0];
                    if (selectedItem?.key && selectedItem.key.toString() !== SharePointFolderContentTypeIds.folder) {
                        await getFields(await authModule.getSharePointAccessToken(), selectedItem.key);
                    }
                }
            }
        }

        setSelectedItem(selectedItem ?? folderOption);
        setIsMultipleOptionsAvailable(multipleOptions);
    };

    const canAddFolderDocSet = React.useCallback(() => {
        let addFolder = false;
        let addDocset = false;

        const isInFolder = ((currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath) || (currentDirectory?.directory?.length > 0 && currentDirectory.parentId));

        if (enableNewTemplateFilters) {
            const rootContentType: string = "root";

            let currentContentype: string;
            if (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                currentContentype = SharePointFolderContentTypeIds.documentSet;
            } else if (isInFolder) {
                currentContentype = SharePointFolderContentTypeIds.folder;
            } else {
                currentContentype = rootContentType;
            }

            if (currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet)) {
                currentContentype = SharePointFolderContentTypeIds.documentSet;
            } else if (isInFolder) {
                currentContentype = SharePointFolderContentTypeIds.folder;
            } else {
                currentContentype = rootContentType;
            }

            if (currentContentype === rootContentType && newTemplateLibraryFilters?.includes(newTemplateFilterEnum.folder)) {
                addFolder = true;
            }
            else if (currentContentype === SharePointFolderContentTypeIds.folder && newTemplateFolderFilters?.includes(newTemplateFilterEnum.folder)) {
                addFolder = true;
            }
            else if (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.folder)) {
                addFolder = true;
            }

            if (currentContentype === rootContentType && newTemplateLibraryFilters?.includes(newTemplateFilterEnum.docset)) {
                addDocset = true;
            }
            else if (currentContentype === SharePointFolderContentTypeIds.folder && newTemplateFolderFilters?.includes(newTemplateFilterEnum.docset)) {
                addDocset = true;
            }
            else if (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.docset)) {
                addDocset = true;
            }
        } else {
            addFolder = true;
            addDocset = true;
        }

        return {
            addFolder,
            addDocset
        }
    }, [enableNewTemplateFilters, newTemplateFolderFilters, newTemplateDocumentSetFilters])

    const handleOnContentTypeChange = async (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption) => {
        setFields([]);
        setSelectedItem(item);
        if (item.key.toString() !== SharePointFolderContentTypeIds.folder) {
            await getFields(await authModule.getSharePointAccessToken(), item.key);
        }
    };

    const handleFolderNameChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void => {
        updateFolderName(newValue);
    };

    const updateFolderName = (newValue: string): void => {
        setFolderName(newValue ? FileFolderNameExtensions.EnsureValidName(newValue.trimStart(), false) : "");
    }

    const handleInputChange = (index: number, value: any, hasError: boolean) => {
        const updatedinputValuesToSave = [...inputValuesToSave];
        updatedinputValuesToSave[index].value = value;
        updatedinputValuesToSave[index].hasError = hasError;
        setInputValuesToSave(updatedinputValuesToSave);
    };

    const getFields = async (accessToken: string, contentTypeId: string | number) => {
        let fields = await SharepointContentTypeHelper.getFields(
            accessToken,
            contentTypeId as string,
            {
                webUrl: currentDirectory.webUrl,
                documentLibrary: currentDirectory.DocumentLibrary,
                sharePointListId: currentDirectory.sharePointListId
            }
        );

        if (hiddenMetadataFields?.length > 0) {
            fields = fields.filter(field => !hiddenMetadataFields.some(hiddenField => field.xml?.DisplayName?.trim().toLowerCase() === hiddenField.trim()));
        }

        let inputValuesToSaveFields: SharePointInputData[] = fields.map((item, index) => setupInputValues(item, index));

        if (currentDirectory.parentId) {
            inputValuesToSaveFields = await updateMetaDataFieldsFromParentContainer(inputValuesToSaveFields);
        }

        setInputValuesToSave(inputValuesToSaveFields);
        setFields(fields);
    };

    const updateMetaDataFieldsFromParentContainer = async (inputValuesToSaveFields: SharePointInputData[]) => {
        let uniqueId = currentDirectory?.sharePointUniqueId;
        if (currentDirectory?.parentId && !uniqueId) {
            uniqueId = await getDriveItemSharepointUniqueId(currentDirectory.parentId, currentDirectory.id);
        }

        let file: any = await getItemByUniqueId({
            siteUrl: currentDirectory.webUrl,
            sharePointListId: currentDirectory.sharePointListId,
            uniqueId: uniqueId,
            accessToken: isInOneDrive.current ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
        }).unwrap();

        let updatedInputValues = inputValuesToSaveFields.map(inputValue => {
            let newValue: any;

            if (inputValue.xml.Type === 'URL' && typeof file[inputValue.internalName] === 'object') {
                newValue = file[inputValue.internalName] ? file[inputValue.internalName]?.Url : inputValue.value;
            }
            else if (inputValue.xml.Type === 'TaxonomyFieldType' && typeof file[inputValue.internalName] === 'object') {
                newValue = file[inputValue.internalName] ? [file[inputValue.internalName]?.Label] : inputValue.value;
            }
            else if (inputValue.xml.Type === 'Lookup') {
                newValue = file[`${inputValue.internalName}Id`] ? [file[`${inputValue.internalName}Id`]] : inputValue.value;
            }
            else if (inputValue.xml.Type === 'LookupMulti') {
                newValue = file[`${inputValue.internalName}Id`] && file[`${inputValue.internalName}Id`]?.length > 0 ? file[`${inputValue.internalName}Id`] : inputValue.value;
            }
            else if (inputValue.xml.Type === 'User') {
                newValue = file[`${inputValue.internalName}Id`] ? [file[`${inputValue.internalName}Id`]] : inputValue.value;
            }
            else if (inputValue.xml.Type === 'UserMulti') {
                newValue = file[`${inputValue.internalName}Id`] && file[`${inputValue.internalName}Id`]?.length > 0 ? file[`${inputValue.internalName}Id`] : inputValue.value;
            }
            else if (inputValue.xml.Type === "DateTime") {
                let dateValue: Date = file[inputValue.internalName] && file[inputValue.internalName]?.length > 0 && new Date(file[inputValue.internalName]);
                if (dateValue) {
                    dateValue = new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate(), dateValue.getHours(), dateValue.getMinutes(), dateValue.getSeconds(), dateValue.getMilliseconds());
                }
                newValue = dateValue || inputValue.value;
            }
            else {
                newValue = file[inputValue.internalName] ?? inputValue.value;
            }

            const updatedValue: SharePointInputData = {
                ...inputValue,
                hasError: inputValue.xml.Required === SharePointMetadataBooleans.true && !newValue,
                value: newValue
            }

            return updatedValue;
        });

        return updatedInputValues
    }

    const setupInputValues = (item, index): SharePointInputData => {
        let initialIsRequiredAndHasNoDefaultValue = item.xml.Required === SharePointMetadataBooleans.true && !item.DefaultValue;
        let defaultValue = DefaultValueService.setValue(item.xml.Type, item.DefaultValue);

        return {
            index: index,
            internalName: item.InternalName,
            value: defaultValue,
            hasError: initialIsRequiredAndHasNoDefaultValue,
            type: item.xml.Percentage ? `${item.xml.Type}Percentage` : item.xml.Type,
            displayName: item.xml.DisplayName,
            xml: item.xml,
            choices: item.Choices
        };
    };

    const createNewFolder = async () => {
        setIsCreatingFolder(true);
        try {
            if (isCurrentDirectorySPList) {
                const data: ListItemCreationData = {
                    accessToken: await authModule.getSharePointAccessToken(),
                    tenantGuid: tenantGuid,
                    accountId,
                    siteUrl: currentDirectory.webUrl,
                    parentUrl: tryEncodeUriComponent(serverRelPath),
                    listId: currentDirectory.sharePointListId,
                    isFolder: true,
                    folderName: folderName?.trimEnd()
                }

                await apiAddListItem(data);
            }
            else if (selectedItem?.key?.toString() === SharePointFolderContentTypeIds.folder) {
                await createFolder(currentDirectory, trimCharacter(folderName?.trimEnd(), "."));
            }
            else {
                const documentSetCreationData: IDocumentSetCreationData = {
                    accessToken: await authModule.getSharePointAccessToken(),
                    tenantGuid: tenantGuid,
                    accountId,
                    siteUrl: currentDirectory.webUrl,
                    parentUrl: `${currentDirectory.webUrl}/${currentDirectory.directory}`,
                    listId: currentDirectory.sharePointListId,
                    contentTypeId: selectedItem?.key as string,
                    teamId: workingDirectories?.find(dir => dir.teamGroupId)?.teamGroupId,
                    channelId: workingDirectories?.find(dir => dir.teamChannelId)?.teamChannelId,
                    name: folderName?.trimEnd(),
                    metadata: inputValuesToSave.map(item => ({ internalName: item.internalName, value: item.value, type: item.type }))
                }
                await createDocumentSet(documentSetCreationData);
            }

            !generalModule.isInConentMangerMode() && customToast.success(languageStrings.FolderCreateSuccess);

            if (onFolderCreated) {
                onFolderCreated();
            } else {
                toggleReload();
                dismissNewFolderPanel();
            }
        } catch (error) {
            customToast.error(languageStrings.ErrorMessageGeneric);
        }
        setIsCreatingFolder(false);
    };

    const checkIsMetadataValid = (): boolean => {
        return getInvalidInputNames().length === 0;
    };

    const getInvalidInputNames = (): string[] => {
        return (folderName?.trim().length > 0 ? [] : [languageStrings.FolderName]).concat(inputValuesToSave.filter(item => item.hasError).map(item => item.displayName));
    }

    const generateInvalidTooltip = (): JSX.Element => {
        const invalidInputNames = getInvalidInputNames();
        if (invalidInputNames.length > 0) {
            return (
                <>
                    <strong>{languageStrings.Required}</strong><br />
                    {invalidInputNames.map((item, index) => <React.Fragment key={index}>&bull; {item} <br /></React.Fragment>)}
                </>
            )
        }
        return null;
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter" && !isCreatingFolder && checkIsMetadataValid()) {
            event.preventDefault();
            createNewFolder();
        }
    };

    const contentTypeFields = fields
        .filter(item => item.xml?.Type !== SharePointInputTypes.thumbNail)
        .map((item, index) => {
            return (
                <Stack.Item key={index} >
                    <InputMetaDataFactory
                        xml={item.xml}
                        choices={item.Choices}
                        webUrl={currentDirectory.webUrl}
                        sharepointService={sharepointService}
                        handleInputChange={handleInputChange}
                        inputData={inputValuesToSave[index]}
                        internalName={item.InternalName}
                        regionalSetting={regionalSettings[currentDirectory.webUrl]}
                        sharepointListId={currentDirectory.sharePointListId}
                        datasourceFields={datasourceFields}
                    />
                </Stack.Item>
            );
        });

    const nameTextFieldCallbackRef = React.useCallback(inputElement => {
        if (inputElement) {
            setTimeout(() => {
                nameTextFieldRef.current?.focus();
            }, 50);
        }
    }, []);

    const folderNameInput = !datasourceItem
        ? <TextField
            elementRef={nameTextFieldCallbackRef}
            componentRef={nameTextFieldRef}
            autoFocus
            required
            // label={`${selectedItem?.text} ${languageStrings.Name}`}
            label={languageStrings.Name}
            value={folderName}
            onChange={handleFolderNameChange}
            onKeyDown={handleKeyDown}
            className="xb-sp-input"
            onGetErrorMessage={() => folderName?.trim().length > 0 ? "" : languageStrings.Required}
            validateOnLoad={false}
        />
        : <ContentTypeNameDatasourcePicker contentTypeInputName={datasourceBoundContentTypeInputName} contentTypeNameValue={folderName} datasourceItem={datasourceItem} updateContentTypeNameValue={updateFolderName} />

    const tooltipProps: ITooltipProps = {
        onRenderContent: generateInvalidTooltip
    };

    const inputFields = (
        <Stack tokens={stackTokens} style={inputStackStyle}>
            {isMultipleOptionsAvailable
                ?
                <>
                    <Stack.Item>
                        <Dropdown
                            placeholder={languageStrings.SelectOption}
                            selectedKey={selectedItem ? selectedItem.key : undefined}
                            onChange={handleOnContentTypeChange}
                            label={languageStrings.FolderType}
                            options={contentTypes}
                            className="xb-sp-input"
                        // responsiveMode={ResponsiveMode.small}
                        />
                    </Stack.Item>
                    {folderNameInput}
                    {contentTypeFields}
                </>
                :
                <>
                    {folderNameInput}
                    {contentTypeFields}
                </>
            }
        </Stack>
    );

    const onRenderFooterContent = (): JSX.Element => (
        <Stack horizontal horizontalAlign="end">
            <TooltipHost tooltipProps={tooltipProps}>
                <PrimaryButton disabled={isCreatingFolder || !checkIsMetadataValid()} text={languageStrings.Create} allowDisabledFocus onClick={() => createNewFolder()}>
                    {isCreatingFolder && <Spinner />}
                </PrimaryButton>
            </TooltipHost>
        </Stack>
    );

    return (
        <Panel
            headerText={selectedItem?.text && `${languageStrings.New} ${selectedItem.text}`}
            isOpen={isOpen}
            styles={panelStyles}
            onDismiss={dismissNewFolderPanel}
            closeButtonAriaLabel="Close"
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom={true}
        >
            {isLoading ?
                <Stack horizontal horizontalAlign="center" style={loaderStackStyle}>
                    <Spinner />
                </Stack>
                :
                <div className={scrollStyle} >
                    <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
                        <div className="xb-content">
                            {inputFields}
                        </div>
                    </ScrollablePane>
                </div>
            }
        </Panel>);
}

export default connector(NewFolderPanel);